import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);
const STORAGE_KEY = "jero-one-cart";

function readCart() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [entries, setEntries] = useState(readCart);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  }, [entries]);

  const value = useMemo(() => ({
    entries,
    count: entries.reduce((sum, e) => sum + e.quantity, 0),
    total: entries.reduce((sum, e) => sum + e.price * e.quantity, 0),
    addItem: (entry) => {
      const key = `${entry.productId}:${entry.itemId}`;
      setEntries((current) => {
        const existing = current.find((item) => item.key === key);
        if (existing) {
          return current.map((item) =>
            item.key === key ? { ...item, quantity: item.quantity + entry.quantity } : item
          );
        }
        return [...current, { ...entry, key }];
      });
    },
    removeItem: (key) => setEntries((current) => current.filter((e) => e.key !== key)),
    setQuantity: (key, quantity) => {
      if (quantity < 1) {
        setEntries((current) => current.filter((e) => e.key !== key));
        return;
      }
      setEntries((current) => current.map((e) => (e.key === key ? { ...e, quantity } : e)));
    },
    clearCart: () => setEntries([]),
  }), [entries]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within CartProvider");
  return context;
}
